// Shell der People-App (DESIGN §3.1, UX §2.2–§2.8): Rail und Bottom-Nav aus den Modul-meta.ts,
// Topbar mit Suche, Registry-Einträgen (Chat), Glocke und Avatar-Menü, Chat-Dock, Login-Gate.
import {
  AccountMenu,
  AppShell,
  BottomNav,
  NavRail,
  ShellIconButton,
  type ShellNavItem,
  TopBar,
} from '@bar/ui/basis';
import { MagnifyingGlass, Question } from '@phosphor-icons/react';
import { useCoreEventInvalidation } from '@web/lib';
import { navItemsFor, useDock, useTopbarItems, useViewer } from '@web/registry';
import { createElement, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { type ShellChrome, useShellChrome } from '../shared/chrome';
import { useSessionActions } from '../shared/data';
import { PlatformShell } from '../shared/gates';
import type { ShellIdentity } from '../shared/identity';
import { activeNavKey, fallbackTitleFor, toShellItems } from '../shared/nav';
import { ShellBell } from '../shared/ShellBell';
import { useGoShortcuts } from '../shared/useGoShortcuts';
import { ShellDock, useChatDockShortcut } from './dock';
import { MoreSheet } from './MoreSheet';

/** Plätze der Bottom-Nav (UX §2.8); der Rest liegt im Sheet „More“. */
const BOTTOM_PATHS = ['/', '/people', '/goals', '/chat'];

function TopbarActions({ chrome }: { chrome: ShellChrome }) {
  const items = useTopbarItems();
  return (
    <>
      <ShellIconButton icon={MagnifyingGlass} label="Search" onClick={chrome.openPalette} />
      {items.map((item) => (
        // Chat & Co. mobil nicht in der Topbar: dort sitzen sie in der Bottom-Nav (UX §2.8).
        <span key={item.key} className="hidden md:contents">
          {createElement(item.Component)}
        </span>
      ))}
      <ShellBell platform="people" />
      <AccountMenu {...chrome.account} />
    </>
  );
}

function PeopleShellBody({ identity }: { identity: ShellIdentity }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const session = useSessionActions();
  useCoreEventInvalidation();
  const viewer = useViewer();
  const all = useMemo(() => navItemsFor('people', viewer), [viewer]);
  const rail = all.filter((i) => i.area === 'rail');
  const railBottom = all.filter((i) => i.area === 'rail-bottom');
  const aliases = identity.employeeId ? { [`/people/${identity.employeeId}`]: '/people/me' } : {};
  const active = activeNavKey([...rail, ...railBottom], pathname, aliases);
  const chrome = useShellChrome(
    'people',
    identity,
    all.filter((i) => i.area === 'rail' || i.area.startsWith('tabs:')),
  );
  useChatDockShortcut(true);
  const dock = useDock();

  const railItems = toShellItems(rail, active);
  const bottomRail: ShellNavItem[] = [
    ...toShellItems(railBottom, active),
    { id: 'help', label: 'Help', icon: Question, href: '#', onSelect: chrome.openPalette },
  ];
  const bottomItems = railItems.filter((i) => BOTTOM_PATHS.includes(i.href));
  const moreItems = railItems.filter((i) => !BOTTOM_PATHS.includes(i.href));

  const goTargets = useMemo(
    () => ({
      h: () => void navigate('/'),
      p: () => void navigate('/people'),
      g: () => void navigate('/goals'),
      r: () => void navigate('/bonus'),
      t: () => void navigate('/tech'),
      c: () => void navigate('/chat'),
      a: () => window.location.assign('/ai/'),
    }),
    [navigate],
  );
  useGoShortcuts(goTargets);

  const adminEntry = chrome.account.entries?.find((e) => e.id === 'to-admin');
  return (
    <AppShell
      rail={<NavRail items={railItems} bottomItems={bottomRail} />}
      topbar={
        <TopBar
          fallbackTitle={fallbackTitleFor([...rail, ...railBottom], active, 'Bar')}
          actions={<TopbarActions chrome={chrome} />}
        />
      }
      bottomNav={
        <BottomNav
          items={bottomItems}
          moreActive={moreItems.some((i) => i.active)}
          more={(close) => (
            <MoreSheet
              items={moreItems}
              admin={
                adminEntry ? { label: adminEntry.label, leading: adminEntry.leading } : undefined
              }
              onSignOut={() => void session.signOut(false)}
              close={close}
            />
          )}
        />
      }
      dock={<ShellDock />}
      dockOpen={dock.panel !== null}
      overlays={chrome.overlays}
    >
      <Outlet />
    </AppShell>
  );
}

export function PeopleShell() {
  return (
    <PlatformShell platform="people">
      {(identity) => <PeopleShellBody identity={identity} />}
    </PlatformShell>
  );
}
